import type { FloorPlan } from '../../api/types.js';
import type { FloorplanPosition, FloorplanSnapshot } from '../../floorplan/types.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface FloorplanMinimapProps {
  floorplan: FloorPlan;
  snapshot: FloorplanSnapshot;
  position: FloorplanPosition | null;
  embed: boolean;
}

export interface FloorplanMinimapCallbacks {
  // SVG-relative pixel coords (in the viewBox space, i.e. image_width_px ×
  // image_height_px). Orchestrator runs pixelToWorld before teleport.
  onTeleport: (pixelXY: [number, number]) => void;
  onClose: () => void;
  // Image load failure → orchestrator dispatches IMAGE_ERROR.
  onImageError: () => void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const TITLE_ID = 'oniri-floorplan-svg-title';
const DESC_ID = 'oniri-floorplan-svg-desc';

interface Refs {
  wrap: HTMLElement;
  imgSlot: HTMLDivElement;
  img: HTMLImageElement;
  errorSlot: HTMLDivElement;
  warning: HTMLDivElement;
  svg: SVGSVGElement;
  dot: SVGCircleElement;
  cone: SVGPathElement;
}

function buildHeadingConePath(
  cx: number,
  cy: number,
  headingDeg: number,
  radius: number,
  fovDeg: number,
): string {
  const half = (fovDeg / 2) * (Math.PI / 180);
  const baseRad = headingDeg * (Math.PI / 180);
  const x1 = cx + Math.cos(baseRad - half) * radius;
  const y1 = cy + Math.sin(baseRad - half) * radius;
  const x2 = cx + Math.cos(baseRad + half) * radius;
  const y2 = cy + Math.sin(baseRad + half) * radius;
  // Sector: from cx,cy to (x1,y1) → arc to (x2,y2) → close back to cx,cy.
  return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radius} ${radius} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
}

export function mountFloorplanMinimap(
  root: HTMLElement,
  initial: FloorplanMinimapProps,
  callbacks: FloorplanMinimapCallbacks,
): ComponentInstance<FloorplanMinimapProps> {
  const wrap = document.createElement('aside');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'region');
  wrap.setAttribute('aria-label', 'Planimetria minimap');
  wrap.style.cssText = initial.embed
    ? 'position:fixed;bottom:calc(0.75rem + env(safe-area-inset-bottom));right:calc(0.75rem + env(safe-area-inset-right));width:14rem;background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.08);border-radius:0.875rem;padding:0.5rem;z-index:11;pointer-events:auto;color:#f4f4f5;font-family:system-ui,sans-serif'
    : 'position:fixed;bottom:calc(1.5rem + env(safe-area-inset-bottom));right:calc(1.5rem + env(safe-area-inset-right));width:18rem;background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.10);border-radius:1rem;padding:0.875rem;z-index:11;pointer-events:auto;color:#f4f4f5;font-family:system-ui,sans-serif;box-shadow:0 12px 32px rgba(0,0,0,0.40)';

  const header = document.createElement('div');
  header.style.cssText =
    'display:flex;align-items:center;justify-content:space-between;margin-bottom:0.5rem';

  const title = document.createElement('h4');
  title.style.cssText =
    'margin:0;font-size:0.6875rem;font-weight:600;color:#d4d4d8;letter-spacing:0.02em';
  setText(title, `Planimetria · ${initial.floorplan.source === 'manual' ? 'Manuale' : 'Auto'}`);
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.classList.add('viewer-ui-tap-target');
  closeBtn.setAttribute('aria-label', 'Chiudi planimetria');
  closeBtn.style.cssText =
    'width:1.25rem;height:1.25rem;border-radius:9999px;background:transparent;color:#a1a1aa;border:0;cursor:pointer;font-size:1rem;line-height:1;padding:0';
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(closeBtn);

  wrap.appendChild(header);

  // Body: relative container with aspect ratio matching the floor plan.
  const body = document.createElement('div');
  const aspect =
    initial.floorplan.image_width_px > 0 && initial.floorplan.image_height_px > 0
      ? `${initial.floorplan.image_width_px} / ${initial.floorplan.image_height_px}`
      : '4 / 3';
  body.style.cssText = `position:relative;width:100%;aspect-ratio:${aspect};background:rgba(0,0,0,0.30);border-radius:0.5rem;overflow:hidden;border:1px solid rgba(255,255,255,0.05)`;

  // HTML <img> background (NOT SVG <image href> — see plan rationale).
  const imgSlot = document.createElement('div');
  imgSlot.style.cssText = 'position:absolute;inset:0';
  const img = document.createElement('img');
  img.src = initial.floorplan.image_url;
  img.alt = '';
  img.style.cssText = 'display:block;width:100%;height:100%;object-fit:contain';
  img.addEventListener('error', () => callbacks.onImageError());
  imgSlot.appendChild(img);
  body.appendChild(imgSlot);

  // Error placeholder (display:none unless imageError). We set static styles
  // via cssText but keep `display` on the property setter — happy-dom (used
  // in unit tests) can fail to read back inline display when toggled after
  // a cssText assignment that also includes display:*.
  const errorSlot = document.createElement('div');
  errorSlot.setAttribute('role', 'status');
  errorSlot.style.cssText =
    'position:absolute;inset:0;align-items:center;justify-content:center;background:rgba(15,15,17,0.65);color:#a1a1aa;font-size:0.6875rem;text-align:center;padding:1rem';
  errorSlot.style.display = 'none';
  errorSlot.textContent = 'Planimetria non caricata';
  body.appendChild(errorSlot);

  // Calibration warning overlay (display:none unless degenerate).
  const warning = document.createElement('div');
  warning.setAttribute('role', 'note');
  warning.style.cssText =
    'position:absolute;inset:auto 0 0 0;background:rgba(127,29,29,0.85);color:#fee2e2;font-size:0.625rem;text-align:center;padding:0.25rem 0.5rem;font-family:ui-monospace,SFMono-Regular,Menlo,monospace';
  warning.style.display = 'none';
  warning.textContent = 'Calibrazione non disponibile · teleport disabilitato';
  body.appendChild(warning);

  // SVG overlay for dot + heading cone.
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute(
    'viewBox',
    `0 0 ${initial.floorplan.image_width_px} ${initial.floorplan.image_height_px}`,
  );
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-labelledby', `${TITLE_ID} ${DESC_ID}`);
  svg.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;pointer-events:auto;cursor:pointer';

  const svgTitle = document.createElementNS(SVG_NS, 'title');
  svgTitle.id = TITLE_ID;
  svgTitle.textContent = 'Planimetria interattiva';
  svg.appendChild(svgTitle);

  const svgDesc = document.createElementNS(SVG_NS, 'desc');
  svgDesc.id = DESC_ID;
  svgDesc.textContent =
    'Click per teletrasportarti nella posizione selezionata. Il dot indica la posizione corrente, il cono indica la direzione di vista.';
  svg.appendChild(svgDesc);

  // Heading cone (drawn behind dot).
  const cone = document.createElementNS(SVG_NS, 'path');
  cone.setAttribute('fill', 'rgba(0,122,255,0.25)');
  cone.setAttribute('stroke', 'rgba(0,122,255,0.6)');
  cone.setAttribute(
    'stroke-width',
    String(Math.max(2, Math.round(initial.floorplan.image_width_px / 200))),
  );
  cone.setAttribute('display', 'none');
  svg.appendChild(cone);

  // Position dot.
  const dot = document.createElementNS(SVG_NS, 'circle');
  const dotR = Math.max(4, Math.round(initial.floorplan.image_width_px / 80));
  dot.setAttribute('r', String(dotR));
  dot.setAttribute('fill', '#007AFF');
  dot.setAttribute('stroke', 'white');
  dot.setAttribute('stroke-width', '2');
  dot.setAttribute('display', 'none');
  svg.appendChild(dot);

  svg.addEventListener('click', (ev) => {
    // Translate client coords to viewBox-space pixel coords.
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const w = initial.floorplan.image_width_px;
    const h = initial.floorplan.image_height_px;
    if (w <= 0 || h <= 0) return;
    // preserveAspectRatio="xMidYMid meet" centers the image inside the
    // svg with letterboxing; recompute the inset.
    const aspectImg = w / h;
    const aspectBox = rect.width / rect.height;
    let drawnW = rect.width;
    let drawnH = rect.height;
    let offsetX = 0;
    let offsetY = 0;
    if (aspectBox > aspectImg) {
      drawnW = rect.height * aspectImg;
      offsetX = (rect.width - drawnW) / 2;
    } else if (aspectBox < aspectImg) {
      drawnH = rect.width / aspectImg;
      offsetY = (rect.height - drawnH) / 2;
    }
    const localX = ev.clientX - rect.left - offsetX;
    const localY = ev.clientY - rect.top - offsetY;
    if (localX < 0 || localY < 0 || localX > drawnW || localY > drawnH) return;
    const px = (localX / drawnW) * w;
    const py = (localY / drawnH) * h;
    callbacks.onTeleport([px, py]);
  });

  body.appendChild(svg);
  wrap.appendChild(body);

  const hint = document.createElement('p');
  hint.style.cssText =
    'margin:0.5rem 0 0;font-size:0.6875rem;color:#a1a1aa;text-align:center;font-family:ui-monospace,SFMono-Regular,Menlo,monospace';
  hint.textContent = 'click per teletrasportarti';
  wrap.appendChild(hint);

  root.appendChild(wrap);

  const refs: Refs = { wrap, imgSlot, img, errorSlot, warning, svg, dot, cone };

  const apply = (props: FloorplanMinimapProps): void => {
    refs.wrap.style.display = props.snapshot.visible ? 'block' : 'none';
    if (!props.snapshot.visible) return;

    // Image error state takes priority — hide dot/cone, show placeholder.
    if (props.snapshot.imageError) {
      refs.imgSlot.style.display = 'none';
      refs.errorSlot.style.display = 'flex';
      refs.dot.setAttribute('display', 'none');
      refs.cone.setAttribute('display', 'none');
      refs.warning.style.display = 'none';
      return;
    }
    refs.imgSlot.style.display = 'block';
    refs.errorSlot.style.display = 'none';

    // Degenerate calibration: image visible, dot/cone hidden, warning shown.
    if (props.snapshot.degenerateCalibration) {
      refs.dot.setAttribute('display', 'none');
      refs.cone.setAttribute('display', 'none');
      refs.warning.style.display = 'block';
      return;
    }
    refs.warning.style.display = 'none';

    // Position not yet emitted by the ticker — keep dot hidden but image visible.
    if (props.position === null) {
      refs.dot.setAttribute('display', 'none');
      refs.cone.setAttribute('display', 'none');
      return;
    }

    refs.dot.setAttribute('cx', props.position.pxX.toFixed(2));
    refs.dot.setAttribute('cy', props.position.pxY.toFixed(2));
    refs.dot.setAttribute('display', 'block');

    // Draw a 60° heading cone with radius proportional to the image short edge.
    const radius = Math.min(props.floorplan.image_width_px, props.floorplan.image_height_px) / 5;
    refs.cone.setAttribute(
      'd',
      buildHeadingConePath(
        props.position.pxX,
        props.position.pxY,
        props.position.headingDeg,
        radius,
        60,
      ),
    );
    refs.cone.setAttribute('display', 'block');
  };

  apply(initial);

  return {
    update(props: FloorplanMinimapProps) {
      apply(props);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
